<!-- The sidebar's Testimonials (Seán, 2026-10-22, issue C1): one at a time, drawn as the profile's
     Testimonial tab draws one (App/Modal/Profile/TestimonialEditor.vue): the portrait beside the
     name and affiliation, then the text in its rounded card; dots below to step. A placeholder
     until the first is chosen (isPlaceholder: marked so). -->
<template>
  <section class="testimonials" aria-roledescription="carousel" aria-label="Testimonials">
    <article
      :key="index"
      aria-roledescription="slide"
      :aria-label="`${index + 1} of ${items.length}`">
      <header>
        <img v-if="current.photo" :src="current.photo" :alt="current.name" class="portrait" />
        <span v-else class="portrait isEmpty" aria-hidden="true" />
        <div>
          <span class="name">{{ current.name }}</span>
          <span class="affiliation">{{ current.affiliation }}</span>
        </div>
      </header>
      <blockquote class="card" :class="{isPlaceholder: current.isPlaceholder}">
        {{ current.text }}
      </blockquote>
    </article>
    <div v-if="items.length > 1" class="dots" role="group" aria-label="Choose a testimonial">
      <button
        v-for="(item, itemIndex) in items"
        :key="itemIndex"
        type="button"
        class="dot"
        :aria-label="item.name"
        :aria-current="itemIndex === index ? 'true' : undefined"
        @click="index = itemIndex" />
    </div>
  </section>
</template>

<script setup>
import {computed, ref} from "vue";
const props = defineProps({items: {type: Array, required: true}}); // sidebar.json TESTIMONIALS
const index = ref(0);
const current = computed(() => props.items[index.value]);
</script>

<style scoped>
header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 0.6rem 0 0.5rem;
}
/* the People thumbnails' 167:241, as tall as the two lines beside it (TestimonialEditor.vue) */
.portrait {
  flex: none;
  height: calc(18px * 1.2 + 1px + 16px * 1.3);
  aspect-ratio: 167 / 241;
  border: 1px solid #ccc;
  object-fit: cover;
}
.portrait.isEmpty {
  background: var(--background-color-subtle);
}
.name {
  display: block;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}
.affiliation {
  display: block;
  margin-top: 1px;
  font-size: 16px;
  line-height: 1.3;
  color: var(--text-color-less-subtle);
}
/* the text in the profile's card: rounded, a light fill, a dark outline (RichTextEditor .bio) */
.card {
  margin: 0;
  padding: 6px 10px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.75);
  background: #ececec;
  border: 1px solid #555;
  border-radius: 12px;
}
.card.isPlaceholder {
  font-style: italic;
}
/* from the text's left edge (Seán, 2026-10-22): the first dot's mark (9 px, centred in its
   24 px target) starts where the text does, the target reaching 7.5 px into the margin */
.dots {
  display: flex;
  justify-content: flex-start;
  gap: 4px;
  margin: 0.4rem 0 0 -7.5px;
  padding: 0;
}
.dot {
  width: 24px;
  height: 24px;
  padding: 0;
  background: radial-gradient(circle, var(--border-color-subtle) 4.5px, transparent 5px);
  border: none;
  border-radius: 50%;
  box-shadow: none;
  cursor: pointer;
}
.dot[aria-current="true"] {
  background: radial-gradient(circle, var(--brand-color) 4.5px, transparent 5px);
}
/* hover: a dot that would show another, a step stronger; the current one as it is (DESIGN.md
   § 15) */
.dot:not([aria-current="true"]):hover {
  background: radial-gradient(circle, var(--text-color-less-subtle) 4.5px, transparent 5px);
}
</style>
